/**
 * featurebase/FeaturebaseContext.tsx
 * React context for Featurebase SDK consent and readiness state.
 *
 * The raw context is NOT exported from the barrel — consumers must use
 * the `useFeaturebase()` hook for type-safe access with a missing-provider guard.
 * Only the FeaturebaseProvider (sibling module) imports this directly.
 *
 */

import { createContext } from 'react';
import type { FeaturebaseConsentState } from './types/consent';
import type { FeaturebaseReadinessState } from './types/readiness';

/**
 * Context value exposed to consumers via `useFeaturebase()`.
 *
 * State:
 * - `consent` — user's consent decision (`'granted' | 'denied' | 'unset'`)
 * - `readiness` — SDK lifecycle state (`'loading' | 'ready' | 'error' | 'unloaded'`)
 * - `feedbackReady` — feedback widget initialization dispatched successfully (`true` after `initFeedbackWidget` succeeds)
 * - `unreadChangelogCount` — number of unread changelog entries reported by the SDK callback
 *
 * Actions:
 * - `grantConsent()` — persist `'granted'` to localStorage, begin SDK boot
 * - `denyConsent()` — persist `'denied'` to localStorage, SDK stays unloaded
 * - `revokeConsent()` — `removeItem` from localStorage (→ `'unset'`), teardown SDK
 * - `retryInitialization()` — re-run boot sequence (consent stays granted).
 *    Returns `true` if retry dispatched, `false` if throttled by cooldown or guarded by state
 * - `openChangelogPopup()` — open the changelog popup widget. No-op when readiness is not `'ready'`
 */
export interface FeaturebaseContextValue {
  consent: FeaturebaseConsentState;
  readiness: FeaturebaseReadinessState;
  feedbackReady: boolean;
  unreadChangelogCount: number;
  grantConsent: () => void;
  denyConsent: () => void;
  revokeConsent: () => void;
  retryInitialization: () => boolean;
  openChangelogPopup: () => void;
}

/**
 * Context with null default — requires FeaturebaseProvider to be mounted.
 */
export const FeaturebaseContext = createContext<FeaturebaseContextValue | null>(null);
